/**
 * versicherer-benachrichtigung-queries.ts - P4-S7: the TanStack Query hooks behind the
 * «Versicherer benachrichtigen» dialog, the notification panel and the Fristen form.
 *
 * One query key per read (lib/query-keys.ts). A send or a close invalidates every
 * Versicherer-Benachrichtigung read, so the panel and the dialog show the new state without a
 * reload, wherever on the page they are mounted.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CloseMandateProcessBody,
  InsurerNotificationFristConfigDto,
  MandateInsurerNotificationDto,
  MandateProcessStateDto,
  SendVersichererBenachrichtigungBody,
  SendVersichererBenachrichtigungResultDto,
  SetInsurerNotificationFristConfigBody,
  VersichererBenachrichtigungDialogDto,
  VersichererBenachrichtigungMandateDto,
} from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';

/** The dialog DTO for a customer: its latest Analysemandat, the rows and the drafts. A pure read. */
export function useVersichererBenachrichtigungDialog(
  subjectType: 'person' | 'firma',
  subjectId: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery<VersichererBenachrichtigungDialogDto>({
    queryKey: qk.versichererBenachrichtigungDialog(subjectType, subjectId),
    queryFn: () => apiClient.versichererBenachrichtigung.dialog(subjectType, subjectId),
    enabled: options.enabled ?? true,
    // A customer without an Analysemandat answers 404: that is the state, not a transient error.
    retry: false,
  });
}

/** The customer's Analysemandat as the dialog would pick it: a light read that keys the panel. */
export function useVersichererBenachrichtigungMandate(
  subjectType: 'person' | 'firma',
  subjectId: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery<VersichererBenachrichtigungMandateDto>({
    queryKey: qk.versichererBenachrichtigungMandate(subjectType, subjectId),
    queryFn: () => apiClient.versichererBenachrichtigung.mandate(subjectType, subjectId),
    enabled: options.enabled ?? true,
  });
}

/** The send (steps 22-24). Refreshes every notification read on success. */
export function useSendVersichererBenachrichtigung() {
  const qc = useQueryClient();
  return useMutation<
    SendVersichererBenachrichtigungResultDto,
    Error,
    { mandateId: string; body: SendVersichererBenachrichtigungBody }
  >({
    mutationFn: ({ mandateId, body }) => apiClient.versichererBenachrichtigung.send(mandateId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.versichererBenachrichtigung() });
    },
  });
}

/** A mandate's notifications (step 24, the Fristüberwachung). */
export function useVersichererBenachrichtigungen(mandateId: string) {
  return useQuery<MandateInsurerNotificationDto[]>({
    queryKey: qk.versichererBenachrichtigungen(mandateId),
    queryFn: () => apiClient.versichererBenachrichtigung.list(mandateId),
  });
}

/** The manual close with a Notiz. Refreshes every notification read, also after a failure. */
export function useSchliessenVersichererBenachrichtigung(mandateId: string) {
  const qc = useQueryClient();
  return useMutation<MandateInsurerNotificationDto, Error, { notificationId: string; notiz: string }>({
    mutationFn: ({ notificationId, notiz }) =>
      apiClient.versichererBenachrichtigung.schliessen(mandateId, notificationId, { notiz }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.versichererBenachrichtigung() });
    },
  });
}

/**
 * The reminder loop of an analysis notification (P4 step 24): «Jetzt erinnern» (the reminder now, from
 * the caller's mailbox) and «Eskalieren» (the task «Versicherer ohne Rückmeldung» now). Refreshes every
 * notification read and the tasks, also after a failure (a lost race moved the row anyway).
 */
export function useVersichererBenachrichtigungReminder(mandateId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { action: 'erinnern' | 'eskalieren' | 'versand_bestaetigt' | 'nicht_versendet'; notificationId: string }>({
    mutationFn: ({ action, notificationId }) => {
      switch (action) {
        case 'erinnern':
          return apiClient.registrations.erinnern(mandateId, notificationId);
        case 'eskalieren':
          return apiClient.registrations.eskalieren(mandateId, notificationId);
        case 'versand_bestaetigt':
          return apiClient.registrations.versandBestaetigt(mandateId, notificationId);
        case 'nicht_versendet':
          return apiClient.registrations.nichtVersendet(mandateId, notificationId);
        default: {
          const unhandled: never = action;
          throw new Error(`unhandled action ${String(unhandled)}`);
        }
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.versichererBenachrichtigung() });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['registrations'] });
    },
  });
}

/** An Analysemandat's process state (step 27); a send or a close above refreshes it too. */
export function useMandateProcessState(mandateId: string) {
  return useQuery<MandateProcessStateDto>({
    queryKey: qk.mandateProcessState(mandateId),
    queryFn: () => apiClient.mandateProcess.state(mandateId),
  });
}

/** Close or reopen the process. Refreshes every Versicherer-Benachrichtigung read, also after a failure. */
export function useMandateProcessAction(mandateId: string) {
  const qc = useQueryClient();
  return useMutation<MandateProcessStateDto, Error, { action: 'close'; body: CloseMandateProcessBody } | { action: 'reopen' }>({
    mutationFn: (v) =>
      v.action === 'close' ? apiClient.mandateProcess.close(mandateId, v.body) : apiClient.mandateProcess.reopen(mandateId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.versichererBenachrichtigung() });
      // The area «Mandate & Eintragungen» shows the Brokermandat's process state.
      void qc.invalidateQueries({ queryKey: ['registrations'] });
    },
  });
}

/** The per-tenant Versicherer-Fristen (Administration). */
export function useVersichererFristen() {
  return useQuery<InsurerNotificationFristConfigDto[]>({
    queryKey: qk.adminVersichererFristen(),
    queryFn: () => apiClient.admin.versichererFristen(),
  });
}

/** Save one prozess row of the Fristen. */
export function useSetVersichererFristen() {
  const qc = useQueryClient();
  return useMutation<InsurerNotificationFristConfigDto, Error, SetInsurerNotificationFristConfigBody>({
    mutationFn: (body) => apiClient.admin.setVersichererFristen(body),
    onSuccess: (saved) => {
      qc.setQueryData<InsurerNotificationFristConfigDto[]>(qk.adminVersichererFristen(), (prev) =>
        prev?.map((r) => (r.prozess === saved.prozess ? saved : r)),
      );
    },
  });
}
